import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, Gift, RefreshCcw, Shuffle, Trash2, User } from 'lucide-react';
import * as XLSX from 'xlsx';
import { CKO2026_TRICKS } from '../constants';
import type { Cko2026WarmupLeaderboardEntry, Cko2026WarmupUploadConfig, User as UserType } from '../types';
import {
  fetchCko2026WarmupLeaderboard,
  notifyCko2026WarmupSubmission,
  fetchCko2026WarmupReports,
  fetchCko2026WarmupUploadConfig,
  removeCko2026WarmupSubmission,
  resolveInvalidCko2026WarmupReport,
  saveCko2026WarmupUploadConfig,
  type Cko2026WarmupReportEntry,
} from '../cko2026Warmup';
import CKO2026WarmupQueuePanel from './CKO2026WarmupQueuePanel';

interface CKO2026WarmupAdminProps {
  user: UserType;
  onBack: () => void;
}

const WARMUP_TRICK_COUNT = CKO2026_TRICKS.filter((trick) => trick.score >= 1).length;
const canAccessWarmupAdmin = (user: UserType) =>
  !!(user.isAdmin || user.roles?.includes('administrator') || user.roles?.includes('author'));

const CKO2026WarmupAdmin: React.FC<CKO2026WarmupAdminProps> = ({ user, onBack: _onBack }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<Cko2026WarmupReportEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<Record<string, boolean>>({});
  const [notifying, setNotifying] = useState<Record<string, boolean>>({});
  const [resolving, setResolving] = useState<Record<string, boolean>>({});
  const [statusMessage, setStatusMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<{ key: string; type: 'remove' | 'notify' | 'resolve' } | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [leaderboard, setLeaderboard] = useState<Cko2026WarmupLeaderboardEntry[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [topPercent, setTopPercent] = useState(10);
  const [activeTab, setActiveTab] = useState<'ranking' | 'reports' | 'queue'>('ranking');
  const [drawResult, setDrawResult] = useState<Cko2026WarmupLeaderboardEntry | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawAnimId, setDrawAnimId] = useState<Cko2026WarmupLeaderboardEntry | null>(null);
  const [drawHistory, setDrawHistory] = useState<Cko2026WarmupLeaderboardEntry[]>([]);
  const [drawFilterPercent, setDrawFilterPercent] = useState(100);
  const [uploadConfig, setUploadConfig] = useState<Cko2026WarmupUploadConfig>({ uploadsEnabled: true, updatedAt: 0 });
  const [uploadConfigLoading, setUploadConfigLoading] = useState(true);
  const [uploadConfigSaving, setUploadConfigSaving] = useState(false);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCko2026WarmupReports();
      setReports(data);
    } catch (err: any) {
      setStatusMessage(err.message || '获取举报列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeaderboard = useCallback(async () => {
    setLeaderboardLoading(true);
    try {
      const entries = await fetchCko2026WarmupLeaderboard();
      setLeaderboard(entries);
    } catch (_) { /* ignore */ }
    finally { setLeaderboardLoading(false); }
  }, []);

  const loadUploadConfig = useCallback(async () => {
    setUploadConfigLoading(true);
    try {
      const config = await fetchCko2026WarmupUploadConfig();
      setUploadConfig(config);
    } catch (err: any) {
      setStatusMessage(err.message || '获取上传开关失败');
    } finally {
      setUploadConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUploadConfig();
  }, [loadUploadConfig]);

  useEffect(() => {
    if (activeTab === 'ranking') {
      setStatusMessage('');
      loadLeaderboard();
      return;
    }

    if (activeTab === 'reports') {
      loadReports();
      return;
    }

    setStatusMessage('');
  }, [activeTab, loadLeaderboard, loadReports]);

  const handleRemove = useCallback(async (targetUserId: string, trickId: string, reason: string) => {
    const key = `${targetUserId}:${trickId}`;
    setConfirmAction(null);
    setActionReason('');
    setStatusMessage('');
    setRemoving(prev => ({ ...prev, [key]: true }));
    try {
      await removeCko2026WarmupSubmission(targetUserId, trickId, reason);
      setReports(prev => prev.filter(r => !(r.userId === targetUserId && r.submission.trickId === trickId)));
      setStatusMessage('该招式视频和对应分数已移除。');
      await loadReports();
    } catch (err: any) {
      setStatusMessage(err.message || '移除失败');
    } finally {
      setRemoving(prev => ({ ...prev, [key]: false }));
    }
  }, [loadReports]);

  const handleNotify = useCallback(async (targetUserId: string, trickId: string, reason: string) => {
    const key = `${targetUserId}:${trickId}`;
    setConfirmAction(null);
    setActionReason('');
    setStatusMessage('');
    setNotifying(prev => ({ ...prev, [key]: true }));
    try {
      await notifyCko2026WarmupSubmission(targetUserId, trickId, reason);
      setReports(prev => prev.filter(r => !(r.userId === targetUserId && r.submission.trickId === trickId)));
      setStatusMessage('管理员提示已发送，用户会在“我的招式”中看到这条通知。');
      await loadReports();
    } catch (err: any) {
      setStatusMessage(err.message || '发送提示失败');
    } finally {
      setNotifying(prev => ({ ...prev, [key]: false }));
    }
  }, [loadReports]);

  const handleResolveReport = useCallback(async (targetUserId: string, trickId: string) => {
    const key = `${targetUserId}:${trickId}`;
    setConfirmAction(null);
    setActionReason('');
    setStatusMessage('');
    setResolving(prev => ({ ...prev, [key]: true }));
    try {
      await resolveInvalidCko2026WarmupReport(targetUserId, trickId);
      setReports(prev => prev.filter(r => !(r.userId === targetUserId && r.submission.trickId === trickId)));
      setStatusMessage('该举报已标记为无效，视频和分数已保留。');
      await loadReports();
    } catch (err: any) {
      setStatusMessage(err.message || '标记举报无效失败');
    } finally {
      setResolving(prev => ({ ...prev, [key]: false }));
    }
  }, [loadReports]);

  const handleUploadToggle = useCallback(async () => {
    if (uploadConfigSaving || uploadConfigLoading) return;
    const nextEnabled = !uploadConfig.uploadsEnabled;
    setUploadConfigSaving(true);
    setStatusMessage('');
    try {
      const nextConfig = await saveCko2026WarmupUploadConfig(nextEnabled);
      setUploadConfig(nextConfig);
      setStatusMessage(nextEnabled ? '已开启预热赛视频上传。' : '已关闭预热赛视频上传，用户端会提示维护中。');
    } catch (err: any) {
      setStatusMessage(err.message || '更新上传开关失败');
    } finally {
      setUploadConfigSaving(false);
    }
  }, [uploadConfig.uploadsEnabled, uploadConfigLoading, uploadConfigSaving]);

  const handleDraw = useCallback(() => {
    if (leaderboard.length === 0 || isDrawing) return;
    setIsDrawing(true);
    setDrawResult(null);
    setDrawAnimId(null);
    setStatusMessage('');

    // Build draw pool based on filter
    const poolSize = Math.ceil(leaderboard.length * drawFilterPercent / 100);
    const pool = leaderboard.slice(0, poolSize);
    if (pool.length === 0) {
      setIsDrawing(false);
      setStatusMessage('当前过滤范围内没有玩家');
      return;
    }

    // Pick winner from filtered pool
    const winner = pool[Math.floor(Math.random() * pool.length)];

    // Animate: cycle through pool entries, then land on winner
    const totalSteps = 20;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      if (step < totalSteps) {
        const randomEntry = pool[Math.floor(Math.random() * pool.length)];
        setDrawAnimId(randomEntry);
      } else if (step === totalSteps) {
        setDrawAnimId(winner);
        setDrawResult(winner);
        setDrawHistory(prev => [winner, ...prev]);
        setIsDrawing(false);
        clearInterval(interval);
      } else {
        clearInterval(interval);
      }
    }, 80);
  }, [leaderboard, isDrawing, drawFilterPercent]);

  const formatTime = (ts: number | undefined) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleExportLeaderboardExcel = useCallback(() => {
    if (leaderboard.length === 0) {
      setStatusMessage('暂无排行榜数据可导出。');
      return;
    }

    const summaryRows = leaderboard.map((entry, index) => {
      const readySubmissions = entry.submissions.filter((submission) => submission.status !== 'failed');
      const processingCount = entry.submissions.filter((submission) => submission.status === 'processing').length;
      const failedCount = entry.submissions.filter((submission) => submission.status === 'failed').length;
      const reportedCount = entry.submissions.filter((submission) => submission.reported).length;
      const likeCount = entry.submissions.reduce((sum, submission) => sum + (submission.likes || 0), 0);

      return {
        'Rank / 排名': index + 1,
        'User ID / 用户ID': entry.userId,
        'Display Name / 昵称': entry.displayName,
        'Total Score / 总分': entry.totalScore,
        'Completed Count / 完成招式数': entry.completedCount,
        'Completion Rate / 完成度': `${entry.completedCount}/${WARMUP_TRICK_COUNT}`,
        'Registered At / 报名时间': formatTime(entry.registeredAt ?? undefined),
        'Updated At / 最后更新时间': formatTime(entry.updatedAt ?? undefined),
        'Submission Count / 投稿总数': entry.submissions.length,
        'Ready Count / 已就绪': readySubmissions.length,
        'Processing Count / 转码中': processingCount,
        'Failed Count / 失败数': failedCount,
        'Reported Count / 被举报数': reportedCount,
        'Likes Total / 获赞总数': likeCount,
      };
    });

    const detailRows = leaderboard.reduce<Array<Record<string, string | number>>>((rows, entry, index) => {
      if (entry.submissions.length === 0) {
        rows.push({
          'Rank / 排名': index + 1,
          'User ID / 用户ID': entry.userId,
          'Display Name / 昵称': entry.displayName,
          'Total Score / 总分': entry.totalScore,
          'Completed Count / 完成招式数': entry.completedCount,
          'Trick ID / 招式ID': '',
          'Trick Name / 招式名': '',
          'Trick Score / 招式分值': '',
          'Status / 状态': '无投稿',
          'Uploaded At / 上传时间': '',
          'Likes / 点赞数': 0,
          'Reported / 是否被举报': '',
          'Reported At / 举报时间': '',
          'Report Reason / 举报原因': '',
          'Video URL / 视频地址': '',
          'Source URL / 原始视频地址': '',
          'File Name / 文件名': '',
          'MIME Type / 媒体类型': '',
        });
        return rows;
      }

      entry.submissions.forEach((submission) => {
        rows.push({
          'Rank / 排名': index + 1,
          'User ID / 用户ID': entry.userId,
          'Display Name / 昵称': entry.displayName,
          'Total Score / 总分': entry.totalScore,
          'Completed Count / 完成招式数': entry.completedCount,
          'Trick ID / 招式ID': submission.trickId,
          'Trick Name / 招式名': submission.trickName,
          'Trick Score / 招式分值': submission.score,
          'Status / 状态': submission.status || 'ready',
          'Uploaded At / 上传时间': formatTime(submission.uploadedAt),
          'Likes / 点赞数': submission.likes || 0,
          'Reported / 是否被举报': submission.reported ? '是' : '否',
          'Reported At / 举报时间': formatTime(submission.reportedAt),
          'Report Reason / 举报原因': submission.reportReason || '',
          'Video URL / 视频地址': submission.videoUrl || '',
          'Source URL / 原始视频地址': submission.sourceUrl || '',
          'File Name / 文件名': submission.fileName || submission.sourceFileName || '',
          'MIME Type / 媒体类型': submission.mimeType || submission.sourceMimeType || '',
        });
      });

      return rows;
    }, []);

    const statsRows = [
      { 'Metric / 指标': '导出时间', 'Value / 数值': formatTime(Date.now()) },
      { 'Metric / 指标': '排行榜人数', 'Value / 数值': leaderboard.length },
      { 'Metric / 指标': `当前前N%筛选`, 'Value / 数值': `${topPercent}%` },
      { 'Metric / 指标': '前N%人数', 'Value / 数值': Math.ceil(leaderboard.length * topPercent / 100) },
      { 'Metric / 指标': '总投稿数', 'Value / 数值': leaderboard.reduce((sum, entry) => sum + entry.submissions.length, 0) },
      { 'Metric / 指标': '总已完成分数', 'Value / 数值': leaderboard.reduce((sum, entry) => sum + entry.totalScore, 0) },
      { 'Metric / 指标': '总完成招式数', 'Value / 数值': leaderboard.reduce((sum, entry) => sum + entry.completedCount, 0) },
      { 'Metric / 指标': '总获赞数', 'Value / 数值': leaderboard.reduce((sum, entry) => sum + entry.submissions.reduce((subSum, submission) => subSum + (submission.likes || 0), 0), 0) },
      { 'Metric / 指标': '被举报投稿数', 'Value / 数值': leaderboard.reduce((sum, entry) => sum + entry.submissions.filter((submission) => submission.reported).length, 0) },
    ];

    const workbook = XLSX.utils.book_new();

    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
    summarySheet['!cols'] = [
      { wch: 10 },
      { wch: 18 },
      { wch: 22 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 20 },
      { wch: 20 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(workbook, summarySheet, '排行榜总表');

    const detailSheet = XLSX.utils.json_to_sheet(detailRows);
    detailSheet['!cols'] = [
      { wch: 10 },
      { wch: 18 },
      { wch: 22 },
      { wch: 12 },
      { wch: 14 },
      { wch: 18 },
      { wch: 42 },
      { wch: 12 },
      { wch: 12 },
      { wch: 20 },
      { wch: 10 },
      { wch: 12 },
      { wch: 20 },
      { wch: 24 },
      { wch: 50 },
      { wch: 50 },
      { wch: 28 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(workbook, detailSheet, '投稿明细');

    const statsSheet = XLSX.utils.json_to_sheet(statsRows);
    statsSheet['!cols'] = [{ wch: 20 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(workbook, statsSheet, '导出说明');

    const date = new Date();
    const stamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}`;
    XLSX.writeFile(workbook, `CKO2026_预热赛排行榜_${stamp}.xlsx`);
    setStatusMessage('排行榜 Excel 已导出。');
  }, [leaderboard, topPercent]);

  if (!canAccessWarmupAdmin(user)) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <div className="text-center">
          <AlertTriangle size={48} className="mx-auto mb-4 text-red-400" />
          <h2 className="text-lg font-semibold text-white">无权限访问</h2>
          <p className="mt-2 text-sm text-zinc-400">仅管理员或作者账户可访问此界面。</p>
          <button
            type="button"
            onClick={() => navigate('/wireless-progress')}
            className="mt-4 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto overflow-x-hidden custom-scrollbar p-3 pt-3 pb-20 md:p-5 md:pb-5">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
        {statusMessage && (
          <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100">
            {statusMessage}
          </div>
        )}
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/wireless-progress')}
            className="rounded-lg border border-white/10 bg-white/5 p-2 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-semibold text-white">
              CKO2026 预热赛 · {activeTab === 'ranking' ? '榜单统计' : activeTab === 'reports' ? '举报管理' : '转码队列'}
            </h1>
            <p className="text-xs text-zinc-500">
              仅管理员/作者可见 · {activeTab === 'ranking' ? '查看榜单前N%选手排名' : activeTab === 'reports' ? '管理被举报的招式视频' : '查看视频转码任务队列'}
            </p>
          </div>
          {activeTab === 'reports' ? (
            <button
              type="button"
              onClick={loadReports}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-50"
            >
              <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} />
              刷新
            </button>
          ) : activeTab === 'ranking' ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportLeaderboardExcel}
                disabled={leaderboardLoading || leaderboard.length === 0}
                className="flex items-center gap-1.5 rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-3 py-1.5 text-xs text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-50"
              >
                <Download size={13} />
                导出 Excel
              </button>
              <button
                type="button"
                onClick={loadLeaderboard}
                disabled={leaderboardLoading}
                className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-50"
              >
                <RefreshCcw size={13} className={leaderboardLoading ? 'animate-spin' : ''} />
                刷新
              </button>
            </div>
          ) : null}
        </div>

        {/* Tab bar */}
        <div className="flex rounded-xl border border-white/10 bg-zinc-900 p-1">
          <button
            type="button"
            onClick={() => setActiveTab('ranking')}
            className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
              activeTab === 'ranking'
                ? 'bg-cyan-500/20 text-cyan-100'
                : 'text-zinc-500 hover:text-white'
            }`}
          >
            榜单统计
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('reports')}
            className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
              activeTab === 'reports'
                ? 'bg-red-500/20 text-red-100'
                : 'text-zinc-500 hover:text-white'
            }`}
          >
            举报管理
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('queue')}
            className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
              activeTab === 'queue'
                ? 'bg-emerald-500/20 text-emerald-100'
                : 'text-zinc-500 hover:text-white'
            }`}
          >
            转码队列
          </button>
        </div>

        {activeTab === 'ranking' ? (
          <>
            <div className="rounded-xl border border-white/10 bg-zinc-900 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-white">用户上传开关</div>
                  <div className="mt-1 text-[11px] text-zinc-500">
                    关闭后，仅订阅者点击预热赛上传按钮时会提示“预热赛维护中，暂时无法上传”，管理员和作者仍可正常上传。
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleUploadToggle}
                  disabled={uploadConfigLoading || uploadConfigSaving}
                  aria-pressed={uploadConfig.uploadsEnabled}
                  className={`inline-flex items-center rounded-full border px-1 py-1 transition-colors disabled:opacity-50 ${
                    uploadConfig.uploadsEnabled
                      ? 'border-emerald-400/30 bg-emerald-500/15'
                      : 'border-red-400/30 bg-red-500/10'
                  }`}
                >
                  <span
                    className={`h-5 w-10 rounded-full transition-colors ${
                      uploadConfig.uploadsEnabled ? 'bg-emerald-400/30' : 'bg-red-400/20'
                    }`}
                  >
                    <span
                      className={`mt-0.5 block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        uploadConfig.uploadsEnabled ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </span>
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 text-[11px]">
                <span className={uploadConfig.uploadsEnabled ? 'text-emerald-300' : 'text-red-300'}>
                  {uploadConfigLoading
                    ? '读取中...'
                    : uploadConfigSaving
                      ? '保存中...'
                      : uploadConfig.uploadsEnabled
                        ? '当前状态：已开启上传'
                        : '当前状态：已关闭上传'}
                </span>
                <span className="text-zinc-600">
                  更新时间：{formatTime(uploadConfig.updatedAt)}
                </span>
              </div>
            </div>

            {/* Top N% Ranking View */}
            <div className="rounded-xl border border-white/10 bg-zinc-900 p-3">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-xs font-semibold text-white">榜单前 N% 统计</div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[11px] text-zinc-500 whitespace-nowrap">前</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={topPercent}
                  onChange={(e) => {
                    const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 1));
                    setTopPercent(v);
                  }}
                  className="w-16 rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1 text-center text-xs text-white outline-none focus:border-cyan-400/50"
                />
                <span className="text-[11px] text-zinc-500 whitespace-nowrap">%</span>
                <span className="ml-2 text-[11px] text-zinc-500">
                  共 {Math.ceil(leaderboard.length * topPercent / 100)} / {leaderboard.length} 人
                </span>
              </div>
              {leaderboardLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCcw size={20} className="animate-spin text-zinc-600" />
                </div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-8 text-[11px] text-zinc-600">暂无排行榜数据</div>
              ) : (
                (() => {
                  const topCount = Math.ceil(leaderboard.length * topPercent / 100);
                  const topPlayers = leaderboard.slice(0, topCount);
                  return (
                    <div className="rounded-lg border border-cyan-400/10 bg-cyan-500/5 p-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                      <table className="w-full text-left text-[11px]">
                        <thead>
                          <tr className="text-zinc-500 border-b border-white/5 sticky top-0 bg-zinc-900/90 backdrop-blur-sm">
                            <th className="pb-1.5 pr-2 font-normal w-8">#</th>
                            <th className="pb-1.5 pr-2 font-normal">选手</th>
                            <th className="pb-1.5 pr-2 font-normal text-right w-14">总分</th>
                            <th className="pb-1.5 font-normal text-right w-12">完成</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topPlayers.map((entry, i) => (
                            <tr key={entry.userId} className="border-b border-white/[0.02] last:border-0 hover:bg-white/[0.02]">
                              <td className="py-1 pr-2 text-zinc-600">{i + 1}</td>
                              <td className="py-1 pr-2">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/wireless-progress/player/${encodeURIComponent(entry.userId)}`)}
                                  className="text-white hover:text-cyan-300 transition-colors text-left truncate block max-w-[280px]"
                                  title={entry.displayName}
                                >
                                  {entry.displayName}
                                </button>
                              </td>
                              <td className="py-1 pr-2 text-right font-orbitron text-xs text-emerald-300">{entry.totalScore}</td>
                              <td className="py-1 text-right text-zinc-500">{entry.completedCount}/{WARMUP_TRICK_COUNT}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()
              )}
            </div>

            {/* Raffle */}
            <div className="rounded-xl border border-yellow-400/20 bg-[linear-gradient(135deg,rgba(250,204,21,0.08),rgba(14,14,14,0.5),rgba(250,204,21,0.04))] p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Gift size={16} className="text-yellow-400" />
                  <div className="text-xs font-semibold text-white">幸运抽奖</div>
                </div>
                <button
                  type="button"
                  onClick={handleDraw}
                  disabled={isDrawing || leaderboard.length === 0}
                  className="flex items-center gap-1.5 rounded-lg border border-yellow-400/30 bg-yellow-500/15 px-3 py-1.5 text-xs text-yellow-100 transition-colors hover:bg-yellow-500/25 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Shuffle size={13} className={isDrawing ? 'animate-spin' : ''} />
                  {isDrawing ? '抽奖中...' : '抽取幸运玩家'}
                </button>
              </div>

              {/* Filter: Top N% */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[11px] text-zinc-500 whitespace-nowrap">抽奖池：前</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={drawFilterPercent}
                  onChange={(e) => {
                    const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 1));
                    setDrawFilterPercent(v);
                  }}
                  className="w-14 rounded-lg border border-yellow-400/20 bg-zinc-800 px-2 py-1 text-center text-xs text-white outline-none focus:border-yellow-400/50"
                />
                <span className="text-[11px] text-zinc-500 whitespace-nowrap">%</span>
                <span className="text-[11px] text-zinc-600">
                  ({Math.ceil(leaderboard.length * drawFilterPercent / 100)} / {leaderboard.length} 人)
                </span>
              </div>

              {/* Animation display */}
              {(isDrawing || drawResult) && (
                <div className="rounded-xl border border-yellow-400/15 bg-black/30 p-4 flex flex-col items-center gap-3 mb-3">
                  {/* Slot-machine style name display */}
                  <div className="text-xs text-zinc-500 font-orbitron uppercase tracking-[0.2em]">
                    {drawResult ? '🎉 中奖玩家' : '抽取中...'}
                  </div>

                  {/* Rolling / Result */}
                  <div className="flex items-center gap-3">
                    <div className={`flex h-14 min-w-[200px] items-center justify-center gap-3 rounded-xl border px-4 transition-all duration-300 ${
                      drawResult
                        ? 'border-yellow-400/40 bg-yellow-500/10 shadow-[0_0_20px_rgba(250,204,21,0.15)]'
                        : 'border-white/10 bg-zinc-900'
                    }`}>
                      {drawAnimId ? (
                        <>
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-yellow-400/40 bg-gradient-to-br from-white/15 to-white/5 text-xs text-white shadow-[0_0_10px_rgba(250,204,21,0.2)] ring-1 ring-yellow-400/20">
                            {drawAnimId.avatarUrl ? (
                              <img src={drawAnimId.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              drawAnimId.displayName.charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className={`truncate text-sm font-bold ${drawResult ? 'text-yellow-100' : 'text-white'}`}>
                              {drawAnimId.displayName}
                            </div>
                            <div className="text-xs font-orbitron text-yellow-300 mt-0.5">
                              {drawAnimId.totalScore}分 · #{leaderboard.findIndex(e => e.userId === drawAnimId.userId) + 1}
                            </div>
                          </div>
                        </>
                      ) : (
                        <span className="text-sm text-zinc-600 font-orbitron">---</span>
                      )}
                    </div>
                  </div>

                  {/* Draw again button */}
                  {drawResult && (
                    <button
                      type="button"
                      onClick={handleDraw}
                      className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      再抽一次
                    </button>
                  )}
                </div>
              )}

              {/* Draw History */}
              {drawHistory.length > 0 && (
                <div className="rounded-xl border border-yellow-400/10 bg-black/20 p-3">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-[11px] text-zinc-500 font-orbitron uppercase tracking-[0.12em]">
                      中奖名单 ({drawHistory.length}人)
                    </div>
                    <button
                      type="button"
                      onClick={() => setDrawHistory([])}
                      className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors"
                    >
                      清空
                    </button>
                  </div>
                  <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                    {drawHistory.map((entry, idx) => {
                      const rank = leaderboard.findIndex(e => e.userId === entry.userId) + 1;
                      return (
                        <div
                          key={`${entry.userId}-${idx}`}
                          className="flex items-center gap-2 rounded-lg border border-white/[0.04] bg-white/[0.02] px-2.5 py-1.5"
                        >
                          <span className="shrink-0 w-5 text-center text-[10px] font-orbitron text-yellow-500/60">
                            {idx + 1}
                          </span>
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full border border-yellow-400/20 bg-gradient-to-br from-white/10 to-white/[0.02] text-[9px] text-white">
                            {entry.avatarUrl ? (
                              <img src={entry.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              entry.displayName.charAt(0).toUpperCase()
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => navigate(`/wireless-progress/player/${encodeURIComponent(entry.userId)}`)}
                            className="flex-1 min-w-0 text-left text-xs text-white hover:text-cyan-300 transition-colors truncate"
                          >
                            {entry.displayName}
                          </button>
                          <span className="shrink-0 text-[10px] font-orbitron text-yellow-300">{entry.totalScore}分</span>
                          <span className="shrink-0 text-[10px] text-zinc-600">#{rank}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : activeTab === 'reports' ? (
          <>
            {/* Stats */}
            <div className="rounded-xl border border-white/10 bg-zinc-900 p-3">
              <div className="text-xs text-zinc-500">
                共 <span className="text-white font-semibold">{reports.length}</span> 条被举报记录
              </div>
            </div>

            {/* Report List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCcw size={24} className="animate-spin text-zinc-600" />
              </div>
            ) : reports.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
                <AlertTriangle size={32} className="mb-3 text-zinc-700" />
                <p className="text-sm">暂无被举报的招式</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {reports.map((report) => {
                  const s = report.submission;
                  const removeKey = `${report.userId}:${s.trickId}`;
                  const reporterLabel = s.reportedBy
                    ? `${s.reportedByDisplayName?.trim() || '用户'} (${s.reportedBy})`
                    : '-';
                  return (
                    <div
                      key={removeKey}
                      className="rounded-xl border border-white/10 bg-zinc-900 p-3"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1 min-w-0">
                          {/* Player info */}
                          <button
                            type="button"
                            onClick={() => navigate(`/wireless-progress/player/${encodeURIComponent(report.userId)}`)}
                            className="flex items-center gap-2 mb-2 group"
                          >
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-gradient-to-br from-white/10 to-white/[0.02] text-[10px] text-white">
                              {report.avatarUrl ? (
                                <img src={report.avatarUrl} alt="" className="h-full w-full object-cover rounded-lg" />
                              ) : (
                                report.displayName.charAt(0).toUpperCase()
                              )}
                            </div>
                            <span className="text-sm font-medium text-white group-hover:text-cyan-300 transition-colors">
                              {report.displayName}
                            </span>
                            <User size={12} className="text-zinc-600" />
                          </button>

                          {/* Submission info */}
                          <div className="rounded-lg border border-red-400/20 bg-red-500/5 p-2.5">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="text-sm font-medium text-white">{s.trickName}</div>
                                <div className="mt-1 text-[10px] text-zinc-500 space-y-0.5">
                                  <div>上传时间：{formatTime(s.uploadedAt)}</div>
                                  <div>举报时间：{formatTime(s.reportedAt)}</div>
                                  <div>举报人：{reporterLabel}</div>
                                  <div className="whitespace-pre-line break-words">举报理由：{s.reportReason?.trim() || '-'}</div>
                                </div>
                              </div>
                              <div className="shrink-0 rounded-lg border border-yellow-400/20 bg-yellow-500/10 px-2 py-0.5 text-xs font-orbitron text-yellow-100">
                                {s.score}分
                              </div>
                            </div>
                            {s.videoUrl && (
                              <video
                                className="mt-2 aspect-video w-full rounded-lg border border-white/10 bg-black"
                                src={s.videoUrl}
                                controls
                                preload="metadata"
                              />
                            )}
                          </div>
                        </div>

                        {/* Remove button */}
                        {confirmAction?.key === removeKey ? (
                          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-64">
                            <input
                              type="text"
                              placeholder={
                                confirmAction.type === 'remove'
                                  ? '请输入移除理由...'
                                  : confirmAction.type === 'notify'
                                    ? '请输入提示文本...'
                                    : '确认举报无效后将保留视频和分数'
                              }
                              value={actionReason}
                              onChange={(e) => setActionReason(e.target.value)}
                              readOnly={confirmAction.type === 'resolve'}
                              className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-2.5 py-1.5 text-xs text-white placeholder:text-zinc-500 outline-none focus:border-red-400/50"
                            />
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setConfirmAction(null)}
                                className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-zinc-400 hover:bg-white/10 sm:flex-none"
                              >
                                取消
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (confirmAction.type === 'remove') {
                                    if (!actionReason.trim()) return;
                                    handleRemove(report.userId, s.trickId, actionReason.trim());
                                  } else if (confirmAction.type === 'notify') {
                                    if (!actionReason.trim()) return;
                                    handleNotify(report.userId, s.trickId, actionReason.trim());
                                  } else {
                                    handleResolveReport(report.userId, s.trickId);
                                  }
                                }}
                                disabled={confirmAction.type !== 'resolve' && !actionReason.trim()}
                                className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed sm:flex-none ${
                                  confirmAction.type === 'remove'
                                    ? 'border border-red-400/40 bg-red-600 hover:bg-red-500'
                                    : confirmAction.type === 'notify'
                                      ? 'border border-yellow-400/40 bg-yellow-600 hover:bg-yellow-500'
                                      : 'border border-emerald-400/40 bg-emerald-600 hover:bg-emerald-500'
                                }`}
                              >
                                {confirmAction.type === 'remove' ? '确认移除' : confirmAction.type === 'notify' ? '发送提示' : '确认无效'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
                            <button
                              type="button"
                              onClick={() => {
                                setConfirmAction({ key: removeKey, type: 'resolve' });
                                setActionReason('举报无效后会保留视频与分数，仅清除举报状态。');
                              }}
                              disabled={resolving[removeKey] || notifying[removeKey] || removing[removeKey]}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300 transition-colors hover:bg-emerald-500/20 hover:text-emerald-200 disabled:opacity-50 sm:flex-none"
                            >
                              <CheckCircle2 size={14} />
                              {resolving[removeKey] ? '处理中...' : '举报无效'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setConfirmAction({ key: removeKey, type: 'notify' });
                                setActionReason('');
                              }}
                              disabled={notifying[removeKey] || resolving[removeKey] || removing[removeKey]}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-yellow-400/30 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-300 transition-colors hover:bg-yellow-500/20 hover:text-yellow-200 disabled:opacity-50 sm:flex-none"
                            >
                              <AlertTriangle size={14} />
                              {notifying[removeKey] ? '发送中...' : '提示'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setConfirmAction({ key: removeKey, type: 'remove' });
                                setActionReason('');
                              }}
                              disabled={removing[removeKey] || resolving[removeKey] || notifying[removeKey]}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-300 transition-colors hover:bg-red-500/20 hover:text-red-200 disabled:opacity-50 sm:flex-none"
                            >
                              <Trash2 size={14} />
                              {removing[removeKey] ? '移除中...' : '移除'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <CKO2026WarmupQueuePanel />
        )}
      </div>
    </div>
  );
};

export default CKO2026WarmupAdmin;
